import * as pdfjsLib from 'pdfjs-dist';
import { PDFDocument, degrees } from 'pdf-lib';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.mjs?url';
import { getExportFileName } from './tagUtils';

// Set up PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

// Normalized crop rectangle: fractions in [0,1] of the page's UNROTATED
// MediaBox, top-left origin (image-style). Undefined = no crop (full page).
export interface CropRect { x: number; y: number; w: number; h: number }

// One "cover the original run with a patch, draw the replacement on top"
// text edit — not a true content-stream edit (see utils/textEdit.ts for why).
// Position/size are captured in PDF user-space (unrotated page coordinates)
// at the moment of editing, from pdf.js's TextItem, so baking never needs to
// re-run text extraction.
export interface TextEdit {
  id: string;
  originalText: string;
  newText: string;
  x: number; y: number;         // baseline origin
  width: number; height: number; // original run's bounding box (cover patch size)
  fontSize: number;
  // pdf.js's TextStyle.fontFamily hint (e.g. "serif"/"sans-serif"/a family
  // name) — not a PDF resource key. pdf.js doesn't expose which /Resources
  // /Font entry a run actually used, so bake time picks by glyph coverage
  // across the page's fonts instead (see utils/textEdit.ts); this hint only
  // steers the on-screen editor font and the standard-font fallback choice.
  fontFamilyHint?: string;
  // Cover-patch and replacement-text colors, as 0-1 RGB triples sampled from
  // the rendered page around the original run (see TextEditModal.tsx) — so a
  // patch over a non-white background blends in instead of leaving a visible
  // white box, and the new text stays legible against it. Missing (edits
  // made before this existed) falls back to white patch / black text.
  backgroundColor?: [number, number, number];
  textColor?: [number, number, number];
}

// A free-standing piece of text placed anywhere on the page (dates, stamps,
// notes) — unlike TextEdit, it isn't tied to an existing text run, so baking
// it needs no cover patch and no page-font matching, just a plain draw.
export interface AddedText {
  id: string;
  text: string;
  x: number; y: number; // baseline origin, PDF user-space
  fontSize: number;
  color: [number, number, number]; // 0-1 RGB
}

export interface ProcessedPage {
  id: number;          // Unique ID
  pageIndex: number;   // 0-indexed page in original PDF
  isDeleted: boolean;  // Marked for deletion
  isBlank: boolean;    // Auto-detected blank
  tag?: string;        // Target filename prefix tag (e.g., 'docs', 'pops')
  rotation: number;    // Rotation in degrees (0, 90, 180, 270)
  isCover?: boolean;   // Phone-scanned cover appended to the buffer (not in the original file)
  coverId?: string;    // Key of the cover's image bytes in IndexedDB (persists across reloads)
  isInserted?: boolean; // PDF page(s) appended from an external file (not in the original)
  insertId?: string;   // Key of the source PDF bytes in IndexedDB (persists across reloads)
  crop?: CropRect;     // Optional rectangular crop, applied as a PDF CropBox
  textEdits?: TextEdit[]; // Cover-and-replace text edits (see utils/textEdit.ts)
  addedTexts?: AddedText[]; // Free-standing added text (see utils/addText.ts)
}

/** Stable signature of the crop set, for deciding when a re-bake is needed. */
export function cropSignature(pages: ProcessedPage[]): string {
  return JSON.stringify(
    pages
      .filter(p => p.crop)
      .map(p => ({ i: p.pageIndex, c: p.crop }))
      .sort((a, b) => a.i - b.i)
  );
}

/** Stable signature of the text-edit set, for deciding when a re-bake is needed. */
export function textEditSignature(pages: ProcessedPage[]): string {
  return JSON.stringify(
    pages
      .filter(p => p.textEdits?.length)
      .map(p => ({ i: p.pageIndex, e: p.textEdits }))
      .sort((a, b) => a.i - b.i)
  );
}

/** Stable signature of the added-text set, for deciding when a re-bake is needed. */
export function addedTextSignature(pages: ProcessedPage[]): string {
  return JSON.stringify(
    pages
      .filter(p => p.addedTexts?.length)
      .map(p => ({ i: p.pageIndex, a: p.addedTexts }))
      .sort((a, b) => a.i - b.i)
  );
}

/**
 * Signature of one tag's current page composition — everything that would
 * change the exported PDF for that section: which pages, in what order
 * (array order = export order, so NOT sorted), plus rotation/crop. Compared
 * against the signature recorded at last export to show saved (unchanged)
 * vs. unsaved (edited since) in the sidebar.
 */
export function sectionSignature(tag: string, pages: ProcessedPage[]): string {
  return JSON.stringify(
    pages
      .filter(p => !p.isDeleted && p.tag === tag)
      .map(p => ({ i: p.pageIndex, r: p.rotation, c: p.crop, t: p.textEdits, a: p.addedTexts }))
  );
}

/**
 * Returns a copy of the buffer with each page's crop applied as a PDF
 * CropBox. Crops are non-destructive metadata — pdf.js renders within the
 * CropBox and pdf-lib copyPages preserves it, so preview, thumbnails and
 * every export path honor crops with no further work. Returns the input
 * unchanged when nothing is cropped (avoids a costly full re-save).
 */
export async function bakeCrops(arrayBuffer: ArrayBuffer, pages: ProcessedPage[]): Promise<ArrayBuffer> {
  const cropped = pages.filter(p => p.crop);
  if (cropped.length === 0) return arrayBuffer;

  const doc = await PDFDocument.load(arrayBuffer.slice(0), { ignoreEncryption: true });
  const total = doc.getPageCount();
  for (const p of cropped) {
    if (p.pageIndex < 0 || p.pageIndex >= total) continue;
    const page = doc.getPage(p.pageIndex);
    const mb = page.getMediaBox();
    const c = p.crop!;
    // top-left-origin fractions → PDF user space (bottom-left origin, y-up)
    const x = mb.x + c.x * mb.width;
    const width = c.w * mb.width;
    const y = mb.y + mb.height * (1 - (c.y + c.h));
    const height = c.h * mb.height;
    page.setCropBox(x, y, width, height);
  }
  const bytes = await doc.save();
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}

/**
 * Appends a scanned image as a real PDF page at the END of the buffer and
 * returns the new buffer plus the appended page's index. Appending (rather
 * than inserting) keeps every existing ProcessedPage.pageIndex valid — the
 * cover's position in the document is controlled by where its entry sits in
 * the pages array, since export and preview follow array order.
 * The page is sized to A4 width with the image's aspect ratio, so covers
 * don't dwarf the scanner-produced pages they sit next to.
 */
export async function appendImagePage(
  arrayBuffer: ArrayBuffer,
  imageBytes: ArrayBuffer,
  mime: string
): Promise<{ buffer: ArrayBuffer; pageIndex: number }> {
  const doc = await PDFDocument.load(arrayBuffer.slice(0), { ignoreEncryption: true });
  const image = mime.includes('png')
    ? await doc.embedPng(imageBytes)
    : await doc.embedJpg(imageBytes);

  const pageWidth = 595.28; // A4 portrait width in points
  const pageHeight = pageWidth * (image.height / image.width);
  const page = doc.addPage([pageWidth, pageHeight]);
  page.drawImage(image, { x: 0, y: 0, width: pageWidth, height: pageHeight });

  const bytes = await doc.save();
  // Slice honoring byteOffset/byteLength — bytes.buffer alone could carry
  // extra bytes if pdf-lib returns a view into a larger allocation.
  const buffer = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
  return { buffer, pageIndex: doc.getPageCount() - 1 };
}

/** True for pages appended after the original file (covers or inserted PDFs). */
export function isAppendedPage(p: ProcessedPage): boolean {
  return !!(p.isCover || p.isInserted);
}

/**
 * Appends every page from `insertBytes` at the END of `arrayBuffer` and
 * returns the new buffer plus each appended page's index. Same append-at-end
 * strategy as appendImagePage — array order controls export/preview position.
 */
export async function appendPdfPages(
  arrayBuffer: ArrayBuffer,
  insertBytes: ArrayBuffer
): Promise<{ buffer: ArrayBuffer; pageIndices: number[] }> {
  const doc = await PDFDocument.load(arrayBuffer.slice(0), { ignoreEncryption: true });
  const src = await PDFDocument.load(insertBytes.slice(0), { ignoreEncryption: true });
  // pdf-lib can't decrypt, so re-saving an encrypted doc here yields unreadable
  // pages. Refuse with a clear message rather than silently corrupting.
  if (doc.isEncrypted) throw new Error('This document is encrypted — re-save it without a password before inserting pages.');
  if (src.isEncrypted) throw new Error('The PDF you are inserting is encrypted — re-save it without a password first.');
  const srcCount = src.getPageCount();
  if (srcCount === 0) throw new Error('The PDF has no pages.');
  const copied = await doc.copyPages(src, Array.from({ length: srcCount }, (_, i) => i));
  const startIdx = doc.getPageCount();
  for (const page of copied) doc.addPage(page);
  const pageIndices = copied.map((_, i) => startIdx + i);
  const bytes = await doc.save();
  const buffer = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
  return { buffer, pageIndices };
}

/**
 * Combines several PDFs (already-decrypted buffers, in the given order)
 * into a single document — all pages from the first file, then all pages
 * from the second, and so on. Used by the "Merge PDFs" welcome-screen
 * action, distinct from normal multi-file open (which queues files as
 * separate tabs instead).
 */
export async function mergePdfBuffers(buffers: ArrayBuffer[]): Promise<ArrayBuffer> {
  if (buffers.length === 0) throw new Error('No PDFs to merge.');
  const merged = await PDFDocument.create();
  for (const buf of buffers) {
    const src = await PDFDocument.load(buf.slice(0), { ignoreEncryption: true });
    // pdf-lib can't decrypt — re-saving an encrypted doc here yields
    // unreadable pages. Callers are expected to decrypt before merging;
    // this is a defensive backstop, not the primary handling.
    if (src.isEncrypted) throw new Error('One of the PDFs is encrypted and could not be decrypted automatically.');
    const count = src.getPageCount();
    if (count === 0) continue;
    const copied = await merged.copyPages(src, Array.from({ length: count }, (_, i) => i));
    for (const page of copied) merged.addPage(page);
  }
  if (merged.getPageCount() === 0) throw new Error('None of the selected PDFs have any pages.');
  const bytes = await merged.save();
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}

/**
 * Normalizes a PDF for loading: re-saves it through pdf-lib (ignoring
 * encryption), stripping encryption and rebuilding a clean structure so
 * pdf.js can render documents that otherwise throw. Returns the original
 * buffer unchanged if pdf-lib can't parse it.
 */
export async function normalizePdf(arrayBuffer: ArrayBuffer): Promise<ArrayBuffer> {
  try {
    const doc = await PDFDocument.load(arrayBuffer.slice(0), { ignoreEncryption: true });
    const bytes = await doc.save();
    return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
  } catch {
    return arrayBuffer;
  }
}

/**
 * Loads a PDF from an ArrayBuffer safely by cloning the buffer.
 */
export async function getPdfPageCount(arrayBuffer: ArrayBuffer): Promise<number> {
  const loadingTask = pdfjsLib.getDocument({ data: new Uint8Array(arrayBuffer.slice(0)) });
  const pdf = await loadingTask.promise;
  return pdf.numPages;
}

/**
 * Centralized loader to load the PDF document once.
 */
export async function loadPdfDocument(arrayBuffer: ArrayBuffer): Promise<any> {
  const loadingTask = pdfjsLib.getDocument({ data: new Uint8Array(arrayBuffer.slice(0)) });
  return loadingTask.promise;
}

/**
 * Renders a specific page of a pre-loaded PDF onto a canvas.
 */
export async function renderPageToCanvas(
  pdfDoc: any,
  pageIndex: number,
  canvas: HTMLCanvasElement,
  scale = 0.3
): Promise<void> {
  const page = await pdfDoc.getPage(pageIndex + 1); // PDF.js pages are 1-indexed

  const viewport = page.getViewport({ scale });
  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('Could not get 2D canvas context');
  }

  canvas.height = viewport.height;
  canvas.width = viewport.width;

  await page.render({
    canvasContext: context,
    viewport: viewport
  }).promise;
}

/**
 * Detects if a page canvas is blank.
 * To handle scanned pages, we crop the outer 5% margin (where feed/binder shadows exist)
 * and analyze if the remaining pixels are above a brightness threshold.
 * 
 * @param canvas The canvas element containing the rendered page
 * @param tolerance Brightness limit (0-255, where 255 is pure white). Default 240.
 * @param blankRatio Minimum percentage of near-white pixels to classify as blank. Default 99%.
 */
export function detectIfPageIsBlank(
  canvas: HTMLCanvasElement,
  tolerance = 242,
  blankRatio = 0.992
): boolean {
  const ctx = canvas.getContext('2d');
  if (!ctx) return false;

  const width = canvas.width;
  const height = canvas.height;

  // Ignore 5% padding around the edges
  const insetX = Math.floor(width * 0.05);
  const insetY = Math.floor(height * 0.05);
  const scanWidth = width - (insetX * 2);
  const scanHeight = height - (insetY * 2);

  if (scanWidth <= 0 || scanHeight <= 0) return false;

  const imageData = ctx.getImageData(insetX, insetY, scanWidth, scanHeight);
  const data = imageData.data;
  let nearWhitePixels = 0;
  const totalPixels = scanWidth * scanHeight;

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    
    // Grayscale brightness (Luma formula)
    const brightness = 0.299 * r + 0.587 * g + 0.114 * b;

    if (brightness >= tolerance) {
      nearWhitePixels++;
    }
  }

  const whitePercent = nearWhitePixels / totalPixels;
  return whitePercent >= blankRatio;
}

/**
 * Splices the original PDF and creates separate binary PDFs for each group.
 * Skips pages marked as deleted.
 */
/**
 * Builds the cleaned "master" copy of the document as ONE PDF: every
 * non-deleted page (scanned covers included) in current array order, with
 * user rotations applied. Exported into the "org scan" folder so the
 * archive keeps a complete cleaned original alongside the split files.
 */
export async function buildCleanedDocument(
  arrayBuffer: ArrayBuffer,
  pages: ProcessedPage[]
): Promise<Uint8Array | null> {
  const activePages = pages.filter(p => !p.isDeleted);
  if (activePages.length === 0) return null;

  const srcDoc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
  const newDoc = await PDFDocument.create();
  const copiedPages = await newDoc.copyPages(srcDoc, activePages.map(p => p.pageIndex));

  copiedPages.forEach((page, idx) => {
    const pageInfo = activePages[idx];
    if (pageInfo && pageInfo.rotation) {
      const currentRotation = page.getRotation().angle;
      page.setRotation(degrees((currentRotation + pageInfo.rotation) % 360));
    }
    newDoc.addPage(page);
  });

  return newDoc.save();
}

/** Thrown when the user cancels a running export; callers reset silently. */
export class ExportCancelled extends Error {
  constructor() { super('Export cancelled'); this.name = 'ExportCancelled'; }
}

export async function processAndSplitPDF(
  arrayBuffer: ArrayBuffer,
  pages: ProcessedPage[],
  exportNames: Record<string, string> = {},
  targetTag?: string,
  onProgress?: (done: number, total: number, fileName: string) => void,
  shouldCancel?: () => boolean
): Promise<{ fileName: string; data: Uint8Array; section: string; pageCount: number }[]> {
  const srcDoc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
  const results: { fileName: string; data: Uint8Array; section: string; pageCount: number }[] = [];

  // Filter active pages with valid tags
  const taggedPages = pages.filter(p => 
    !p.isDeleted && 
    p.tag && 
    p.tag.trim() !== '' &&
    (!targetTag || p.tag.trim().toLowerCase() === targetTag.trim().toLowerCase())
  );

  // Identify unique tags in original sequence order (preserve casing from first occurrence)
  const uniqueTags: string[] = [];
  for (const page of taggedPages) {
    const tag = page.tag!.trim();
    if (!uniqueTags.some(t => t.toLowerCase() === tag.toLowerCase())) {
      uniqueTags.push(tag);
    }
  }

  for (const tag of uniqueTags) {
    if (shouldCancel?.()) throw new ExportCancelled();
    const pagesForTag = taggedPages.filter(p => p.tag!.trim().toLowerCase() === tag.toLowerCase());
    const activePageIndices = pagesForTag.map(p => p.pageIndex);
    onProgress?.(results.length, uniqueTags.length, `${getExportFileName(tag, exportNames)}.pdf`);
    // Let the progress toast paint before the synchronous pdf-lib work.
    await new Promise(r => setTimeout(r, 0));

    // Create a new PDF document
    const newDoc = await PDFDocument.create();

    // Copy pages
    const copiedPages = await newDoc.copyPages(srcDoc, activePageIndices);
    
    // Append pages and apply the user rotation angles
    copiedPages.forEach((page, idx) => {
      const pageInfo = pagesForTag[idx];
      if (pageInfo && pageInfo.rotation) {
        const currentRotation = page.getRotation().angle;
        const newRotation = (currentRotation + pageInfo.rotation) % 360;
        page.setRotation(degrees(newRotation));
      }
      newDoc.addPage(page);
    });

    // Serialize to bytes
    const pdfBytes = await newDoc.save();
    const fileName = `${getExportFileName(tag, exportNames)}.pdf`;

    results.push({
      fileName,
      data: pdfBytes,
      section: tag,
      pageCount: pagesForTag.length,
    });
  }

  return results;
}
